import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential, signOut } from "firebase/auth";
import { doc, deleteDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { auth, database, storage } from "../Firebase";

export default function SupprimerCompte() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const handleClose = () => {
    setShowModal(false);
    setPassword("");
    setErrorMessage("");
  };
  
  const handleShow = () => setShowModal(true);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const reauthenticateUser = async (password) => {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error("Aucun utilisateur connecté");
      }
      
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      return true;
    } catch (error) {
      console.error("Erreur d'authentification:", error);
      if (error.code === 'auth/wrong-password') {
        throw new Error("Mot de passe incorrect");
      } else {
        throw error;
      }
    }
  };

  const deleteUserData = async (userId) => {
    try {
      // Supprimer les données Firestore
      await deleteDoc(doc(database, "users", userId));
      
      // Supprimer la photo de profil dans le Storage
      try {
        const photoRef = ref(storage, `profilePics/${userId}`);
        await deleteObject(photoRef);
      } catch (photoError) {
        // Si la photo n'existe pas, on ignore l'erreur
        console.log("Pas de photo de profil à supprimer ou erreur:", photoError);
      }
      
      return true;
    } catch (error) {
      console.error("Erreur lors de la suppression des données:", error);
      throw error;
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!password) {
      setErrorMessage("Veuillez entrer votre mot de passe");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Aucun utilisateur connecté");
      }
      
      // Réauthentifier l'utilisateur
      await reauthenticateUser(password);
      
      // Supprimer les données de l'utilisateur
      await deleteUserData(user.uid);
      
      // Supprimer le compte d'authentification
      await deleteUser(user);
      
      // Afficher le message de succès
      setShowSuccess(true);
      
      // Rediriger vers la page d'accueil après un délai
      setTimeout(() => {
        signOut(auth);
        navigate("/");
      }, 3000);
      
    } catch (error) {
      console.error("Erreur de suppression du compte:", error);
      setErrorMessage(error.message || "Une erreur s'est produite lors de la suppression du compte");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="my-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card>
            <Card.Body className="p-4">
              <h2 className="text-center mb-4">Supprimer votre compte</h2>
              
              {showSuccess && (
                <Alert variant="success" className="mb-4">
                  Votre compte a été supprimé avec succès. Vous allez être redirigé vers la page d'accueil.
                </Alert>
              )}
              
              {!showSuccess && (
                <>
                  <Alert variant="warning">
                    <Alert.Heading>Attention !</Alert.Heading>
                    <p>
                      La suppression de votre compte est définitive et irréversible.
                      Toutes vos données personnelles, votre historique et vos préférences seront perdus.
                    </p>
                  </Alert>
                  
                  <div className="mb-4">
                    <h5>Conséquences de la suppression :</h5>
                    <ul>
                      <li>Perte de toutes vos données personnelles</li>
                      <li>Perte de votre historique d'utilisation</li>
                      <li>Perte de vos points et avantages accumulés</li>
                      <li>Impossibilité de récupérer votre compte</li>
                    </ul>
                  </div>
                  
                  <Button
                    variant="danger"
                    onClick={handleShow}
                    className="w-100"
                  >
                    Je souhaite supprimer mon compte
                  </Button>
                </>
              )}
              
              {/* Modal de confirmation */}
              <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Confirmation de suppression</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>
                    Pour confirmer la suppression de votre compte, veuillez entrer votre mot de passe :
                  </p>
                  
                  {errorMessage && (
                    <Alert variant="danger" className="mb-3">
                      {errorMessage}
                    </Alert>
                  )}
                  
                  <Form onSubmit={handleDeleteAccount}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mot de passe</Form.Label>
                      <Form.Control
                        type="password"
                        value={password}
                        onChange={handlePasswordChange}
                        required
                        placeholder="Entrez votre mot de passe"
                      />
                    </Form.Group>
                    
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="secondary" onClick={handleClose}>
                        Annuler
                      </Button>
                      <Button
                        variant="danger"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Suppression en cours..." : "Supprimer définitivement"}
                      </Button>
                    </div>
                  </Form>
                </Modal.Body>
              </Modal>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}